import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format, addDays, parse, startOfDay } from "date-fns";
import { 
  CalendarIcon, Compass, Users, Clock, DollarSign, Upload, 
  FileText, X, CheckCircle, AlertTriangle, ArrowLeft, MapPin,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  getCabin, getActiveGuest, getTours, createTourReservation, 
  getGuestTourReservations, cancelTourReservation, uploadPassport 
} from "@/lib/api";

export default function TourReservation() {
  const { hotelId, cabinId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [cabinInfo, setCabinInfo] = useState(null);
  const [guest, setGuest] = useState(null);
  const [tours, setTours] = useState([]);
  const [tourReservations, setTourReservations] = useState([]);
  
  // Form state
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [numParticipants, setNumParticipants] = useState(2);
  const [participantNames, setParticipantNames] = useState([""]);
  const [passportFiles, setPassportFiles] = useState([]);
  const [depositConfirmed, setDepositConfirmed] = useState(false);
  const [specialRequests, setSpecialRequests] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);

  useEffect(() => {
    loadData();
  }, [hotelId, cabinId]);

  // Update participant names array when count changes
  useEffect(() => {
    setParticipantNames(prev => {
      const newNames = [...prev];
      while (newNames.length < numParticipants) {
        newNames.push("");
      }
      return newNames.slice(0, numParticipants);
    });
  }, [numParticipants]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cabin info
      const cabinRes = await getCabin(hotelId, cabinId);
      setHotelInfo(cabinRes.data.hotel);
      setCabinInfo(cabinRes.data.cabin);
      
      // Check if this is Bird Brain Bungalows
      if (hotelId !== "bird-brain-bungalows") {
        toast.error("Tours are only available for Bird Brain Bungalows guests");
        navigate(`/reserve/${hotelId}/${cabinId}`);
        return;
      }
      
      // Check for existing guest
      const guestRes = await getActiveGuest(hotelId, cabinId);
      if (!guestRes.data) {
        toast.error("Please register first before booking tours");
        navigate(`/reserve/${hotelId}/${cabinId}`);
        return;
      }
      setGuest(guestRes.data);
      
      // Load available tours
      const toursRes = await getTours();
      setTours(toursRes.data);
      
      // Load guest's tour reservations
      const tourResRes = await getGuestTourReservations(guestRes.data.id);
      setTourReservations(tourResRes.data);
      
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 404) {
        toast.error("Invalid cabin. Please scan the QR code again.");
        navigate("/");
      } else {
        toast.error("Failed to load tour data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePassportUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPassport(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const res = await uploadPassport(file);
        uploadedUrls.push(res.data.file_url);
      }
      setPassportFiles(prev => [...prev, ...uploadedUrls]);
      toast.success(`${files.length} passport(s) uploaded`);
    } catch (error) {
      console.error("Error uploading passport:", error);
      toast.error(error.response?.data?.detail || "Failed to upload passport");
    } finally {
      setUploadingPassport(false);
    }
  };

  const removePassport = (index) => {
    setPassportFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBookTour = async () => {
    if (!selectedTour) {
      toast.error("Please select a tour");
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (numParticipants < selectedTour.min_participants) {
      toast.error(`Minimum ${selectedTour.min_participants} participants required`);
      return;
    }
    if (selectedTour.requires_passport && passportFiles.length < numParticipants) {
      toast.error(`Please upload passport for all ${numParticipants} participants`);
      return;
    }
    if (selectedTour.deposit_required && !depositConfirmed) {
      toast.error("Please confirm you have paid the deposit via PayPal");
      return;
    }

    try {
      setSubmitting(true);
      
      await createTourReservation({
        guest_id: guest.id,
        tour_id: selectedTour.id,
        date: format(selectedDate, "yyyy-MM-dd"),
        num_participants: numParticipants,
        participant_names: participantNames.filter(n => n.trim()),
        passport_files: passportFiles,
        deposit_confirmed: depositConfirmed,
        special_requests: specialRequests.trim()
      });
      
      toast.success("Tour reservation submitted! Awaiting confirmation.");
      
      // Reset form
      setSelectedTour(null);
      setSelectedDate(null);
      setNumParticipants(2);
      setParticipantNames([""]);
      setPassportFiles([]);
      setDepositConfirmed(false);
      setSpecialRequests("");
      
      // Reload reservations
      const tourResRes = await getGuestTourReservations(guest.id);
      setTourReservations(tourResRes.data);
      
    } catch (error) {
      console.error("Error booking tour:", error);
      toast.error(error.response?.data?.detail || "Failed to book tour");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTour = async (reservationId) => {
    if (!confirm("Are you sure you want to cancel this tour? Note: Any deposit paid is non-refundable.")) {
      return;
    }
    
    try {
      await cancelTourReservation(reservationId);
      toast.success("Tour reservation cancelled");
      
      // Reload reservations
      const tourResRes = await getGuestTourReservations(guest.id);
      setTourReservations(tourResRes.data);
    } catch (error) {
      console.error("Error cancelling tour:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel tour");
    }
  };

  const calculateTotalPrice = () => {
    if (!selectedTour) return 0;
    return selectedTour.price * numParticipants;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      confirmed: "bg-green-100 text-green-800 border-green-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
      cancelled: "bg-gray-100 text-gray-800 border-gray-300"
    };
    return statusStyles[status] || statusStyles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Compass className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading tours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background texture-overlay">
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to={`/reserve/${hotelId}/${cabinId}`}
            className="inline-flex items-center text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Restaurant Reservations
          </Link>
          <h1 className="font-display text-4xl font-bold text-foreground mb-2">
            Bird Brain Bungalows Tours
          </h1>
          <p className="text-muted-foreground">
            Book amazing adventures in the Osa Peninsula
          </p>
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mt-3">
            <span className="text-primary font-medium" data-testid="guest-info">
              {guest?.guest_name} • {cabinInfo?.name}
            </span>
          </div>
        </div>

        {/* Your Tour Reservations */}
        {tourReservations.length > 0 && (
          <Card className="mb-6 bg-card border-border shadow-card border-l-4 border-l-primary" data-testid="tour-reservations-card">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Compass className="w-5 h-5 text-primary" />
                Your Tour Reservations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tourReservations.map((res) => (
                  <div 
                    key={res.id}
                    className="p-4 rounded-lg bg-background/50 border border-border"
                    data-testid={`tour-reservation-${res.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-lg">{res.tour_name}</h4>
                        <p className="text-muted-foreground">
                          {format(parse(res.date, "yyyy-MM-dd", new Date()), "EEEE, MMMM d, yyyy")}
                        </p>
                      </div>
                      <Badge className={cn("border", getStatusBadge(res.status))}>
                        {res.status.charAt(0).toUpperCase() + res.status.slice(1)}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>{res.num_participants} participants</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        <span>${res.total_price.toFixed(2)} total</span>
                      </div>
                    </div>
                    
                    {res.status === "confirmed" && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-green-800">Confirmed!</p>
                            <p className="text-sm text-green-700">{res.confirmation_message}</p>
                            <p className="text-sm text-green-700 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Pickup: {res.pickup_time}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {res.status === "pending" && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-yellow-800">Awaiting Confirmation</p>
                            <p className="text-sm text-yellow-700">
                              We'll confirm your booking soon.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {res.deposit_required && (
                      <p className="text-sm text-muted-foreground">
                        Deposit: ${res.deposit_amount.toFixed(2)} 
                        {res.deposit_confirmed && <span className="text-green-600 ml-2">✓ Paid</span>}
                      </p>
                    )}
                    
                    {res.status === "pending" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelTour(res.id)}
                        className="mt-2 text-destructive border-destructive hover:bg-destructive/10"
                        data-testid={`cancel-tour-${res.id}`}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel Reservation
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Tours */}
        <Card className="mb-6 bg-card border-border shadow-card" data-testid="tours-list-card">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Compass className="w-5 h-5 text-primary" />
              Available Tours
            </CardTitle>
            <CardDescription>
              Select a tour to book your adventure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tours.map((tour) => (
                <div
                  key={tour.id}
                  onClick={() => tour.available && setSelectedTour(tour)}
                  className={cn(
                    "p-4 rounded-lg border cursor-pointer transition-all",
                    !tour.available && "opacity-50 cursor-not-allowed",
                    selectedTour?.id === tour.id
                      ? "bg-primary/20 border-primary shadow-glow"
                      : "bg-background/50 border-border hover:border-primary/50"
                  )}
                  data-testid={`tour-card-${tour.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{tour.name}</h4>
                    <span className="text-primary font-bold">${tour.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{tour.description}</p>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline" className="bg-background/50">
                      <Clock className="w-3 h-3 mr-1" />
                      {tour.duration}
                    </Badge>
                    <Badge variant="outline" className="bg-background/50">
                      <Users className="w-3 h-3 mr-1" />
                      {tour.min_participants}-{tour.max_participants} people
                    </Badge>
                    {tour.lunch_included && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        Lunch included
                      </Badge>
                    )}
                    {tour.deposit_required && (
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                        ${tour.deposit_amount} deposit
                      </Badge>
                    )}
                    {tour.requires_passport && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        <FileText className="w-3 h-3 mr-1" />
                        Passport required
                      </Badge>
                    )}
                  </div>
                  
                  {!tour.available && (
                    <Badge className="mt-2 bg-red-100 text-red-700">
                      Currently Unavailable
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Booking Form - Shows when tour is selected */}
        {selectedTour && (
          <Card className="mb-6 bg-card border-border shadow-card" data-testid="booking-form-card">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Book: {selectedTour.name}
              </CardTitle>
              <CardDescription>
                {selectedTour.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Selection */}
              <div className="space-y-2">
                <Label>Tour Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-background/50",
                        !selectedDate && "text-muted-foreground"
                      )}
                      data-testid="tour-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-card border-border">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => {
                        const today = startOfDay(new Date());
                        return date <= today;
                      }}
                      initialFocus
                    />
                    <div className="p-3 border-t border-border">
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => setCalendarOpen(false)}
                        disabled={!selectedDate}
                      >
                        Ok
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Number of Participants */}
              <div className="space-y-2">
                <Label>Number of Participants</Label>
                <Input
                  type="number"
                  min={selectedTour.min_participants}
                  max={selectedTour.max_participants}
                  value={numParticipants}
                  onChange={(e) => setNumParticipants(Math.max(selectedTour.min_participants, Math.min(selectedTour.max_participants, parseInt(e.target.value) || selectedTour.min_participants)))}
                  className="bg-background/50"
                  data-testid="num-participants-input"
                />
                <p className="text-xs text-muted-foreground">
                  Min: {selectedTour.min_participants}, Max: {selectedTour.max_participants}
                </p>
              </div>

              {/* Participant Names */}
              <div className="space-y-2">
                <Label>Participant Names</Label>
                {participantNames.map((name, index) => (
                  <Input
                    key={index}
                    value={name}
                    onChange={(e) => {
                      const newNames = [...participantNames];
                      newNames[index] = e.target.value;
                      setParticipantNames(newNames);
                    }}
                    placeholder={`Participant ${index + 1} name`}
                    className="bg-background/50"
                    data-testid={`participant-name-${index}`}
                  />
                ))}
              </div>

              {/* Passport Upload - if required */}
              {selectedTour.requires_passport && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Passport Upload (Required for Corcovado)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Upload a clear photo or scan of each participant's passport. Required for park entry.
                  </p>
                  
                  {/* Uploaded files */}
                  {passportFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {passportFiles.map((file, index) => (
                        <Badge key={index} variant="outline" className="bg-green-50 text-green-700 border-green-300">
                          Passport {index + 1}
                          <button
                            onClick={() => removePassport(index)}
                            className="ml-2 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      multiple
                      onChange={handlePassportUpload}
                      disabled={uploadingPassport}
                      className="bg-background/50"
                      data-testid="passport-upload"
                    />
                    {uploadingPassport && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                  
                  {passportFiles.length < numParticipants && (
                    <p className="text-xs text-yellow-600">
                      {numParticipants - passportFiles.length} more passport(s) needed
                    </p>
                  )}
                </div>
              )}

              {/* Deposit Info & Confirmation */}
              {selectedTour.deposit_required && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Deposit Required: ${selectedTour.deposit_amount.toFixed(2)}
                  </h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    Please send the deposit via PayPal to: <strong>birdbrain@example.com</strong>
                    <br />
                    Include your name and tour date in the payment note.
                  </p>
                  <p className="text-sm text-yellow-700 mb-3">
                    <strong>Note:</strong> {selectedTour.cancellation_policy}
                  </p>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={depositConfirmed}
                      onCheckedChange={setDepositConfirmed}
                      data-testid="deposit-confirmed-checkbox"
                    />
                    <span className="text-sm">I have paid the deposit via PayPal</span>
                  </label>
                </div>
              )}

              {/* Special Requests */}
              <div className="space-y-2">
                <Label>Special Requests (Optional)</Label>
                <Textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special needs or requests..."
                  className="bg-background/50 resize-none"
                  rows={2}
                  data-testid="special-requests-textarea"
                />
              </div>

              {/* Price Summary */}
              <div className="bg-accent/50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-muted-foreground">
                      Total for {numParticipants} participant(s)
                    </span>
                    {selectedDate && (
                      <p className="text-sm text-muted-foreground">
                        {format(selectedDate, "MMM d, yyyy")} • {selectedTour.pickup_time}
                      </p>
                    )}
                  </div>
                  <span className="text-2xl font-bold text-primary" data-testid="total-price">
                    ${calculateTotalPrice().toFixed(2)}
                  </span>
                </div>
                {selectedTour.deposit_required && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Deposit: ${selectedTour.deposit_amount.toFixed(2)} • 
                    Balance due: ${(calculateTotalPrice() - selectedTour.deposit_amount).toFixed(2)}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleBookTour}
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="book-tour-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Tour Reservation"
                )}
              </Button>

              {/* Tour Details */}
              <div className="bg-background/50 rounded-lg p-4 text-sm">
                <h5 className="font-semibold mb-2">Tour Details</h5>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• <strong>Duration:</strong> {selectedTour.duration}</li>
                  <li>• <strong>Pickup Time:</strong> {selectedTour.pickup_time}</li>
                  <li>• <strong>Cancellation:</strong> {selectedTour.cancellation_policy}</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
